// packages/front/src/portfolio/modals/add-exchange-rate-modal.tsx

import { useState, type FC, type FormEvent } from "react";
import { Button, Modal, Select } from "../../_common";
import { CURRENCY_LIST } from "@signals/shared";
import { useCreateExchangeRate } from "../portfolio.hooks";

export interface AddExchangeRateModalProps {
  open: boolean;
  onClose: () => void;
}

const inputClass =
  "h-9 rounded-md border border-input bg-background px-3 py-1 text-sm";
const numberClass =
  "h-9 rounded-md border border-input bg-background px-3 py-1 text-sm [appearance:textfield]";
const labelClass = "text-sm font-medium";

export const AddExchangeRateModal: FC<AddExchangeRateModalProps> = ({
  open,
  onClose,
}) => {
  const createRate = useCreateExchangeRate();
  const saving = createRate.isPending;

  const [from, setFrom] = useState("");
  const [fromError, setFromError] = useState<string | null>(null);
  const [to] = useState("USD");
  const [rate, setRate] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!from) {
      setFromError("Select currency");
      return;
    }
    setFromError(null);
    try {
      await createRate.mutateAsync({
        fromCurrency: from,
        to,
        rate: Number(rate) || 0,
        date,
        source: "manual",
      });
      onClose();
    } catch {
      // error available on createRate.error
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add Manual Exchange Rate">
      <p className="text-xs text-muted-foreground">
        For manual corrections. Use &ldquo;Refresh Rates&rdquo; to fetch from
        FMP automatically.
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <label className={labelClass}>From</label>
        <Select
          className={inputClass}
          value={from}
          onChange={(value) => {
            setFrom(value);
            setFromError(null);
          }}
          placeholder="Select currency"
          options={CURRENCY_LIST.map((c) => ({ value: c, label: c }))}
          ariaLabel="From currency"
        />
        {fromError && <p role="alert" className="text-sm text-destructive">{fromError}</p>}

        <label className={labelClass}>To</label>
        <input
          className={inputClass}
          value={to}
          readOnly
          aria-readonly="true"
        />

        <label className={labelClass}>Rate</label>
        <input
          className={numberClass}
          type="number"
          step="any"
          value={rate}
          onChange={(e) => setRate(e.target.value)}
          required
        />

        <label className={labelClass}>Date</label>
        <input
          className={inputClass}
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          required
        />

        <div className={inputClass}>Source: manual</div>

        <div className="mt-2 flex gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
